// FILE: CollegeStudentManagement.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Upload, Users, FileText } from 'lucide-react';
import CollegeStudentBulkUpload from './CollegeStudentBulkUpload';
import CollegeStudentDirectory from './CollegeStudentDirectory';
import CollegeUploadLogs from './CollegeUploadLogs';
import { EditStudentModal, ResetPasswordModal, DeleteStudentModal, RestoreStudentModal } from './CollegeStudentModals';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import CollegeFooter from './CollegeFooter';

const T = {
  border:  '1px solid var(--border-subtle, #E2E8F0)',
  radius:  { sm: '8px', md: '10px', lg: '14px', xl: '18px' },
  shadow:  { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)' },
  brand:   'var(--brand-primary, var(--primary-blue, #4F46E5))',
};

const CollegeStudentManagement = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'directory');
  const [collegeId, setCollegeId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.id && user.role !== 'student' && !user.roll_number) {
      setCollegeId(user.id);
    }
  }, []);

  const handleUploadComplete = () => {
    setActiveTab('directory');
    setRefreshKey(prev => prev + 1);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setActiveModal('edit');
  };

  const handleResetPassword = (student) => {
    setSelectedStudent(student);
    setActiveModal('reset');
  };

  const handleDeleteStudent = (student) => {
    setSelectedStudent(student);
    setActiveModal('delete');
  };

  const handleRestoreStudent = (student) => {
    setSelectedStudent(student);
    setActiveModal('restore');
  };

  const handleModalSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedStudent(null);
  };

  // -- Access denied / not logged in -----------------------------------------
  if (!collegeId) {
    const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
    const isStudent = user && (user.role === 'student' || user.roll_number);
    return (
      <div className="college-main-layout" style={{ background: 'var(--bg-main, #F8FAFC)' }}>
        <CollegeHeader />
        <div className="college-content-wrapper">
          <CollegeHomeSideBar />
          <main className="college-main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{
              background: '#FFFFFF',
              border: T.border,
              borderRadius: T.radius.xl,
              boxShadow: T.shadow.xs,
              padding: '48px 40px',
              textAlign: 'center',
              maxWidth: 400,
              width: '100%',
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: '14px',
                background: '#FEF2F2',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <Users size={26} style={{ color: '#DC2626' }} />
              </div>
              {isStudent ? (
                <>
                  <p style={{ color: '#1E293B', fontWeight: 700, fontSize: '1.1rem', marginBottom: 6 }}>Access Denied</p>
                  <p style={{ color: '#64748B', fontSize: '0.875rem', lineHeight: 1.6 }}>
                    This page is for college administrators only. Please log in with your admin credentials.
                  </p>
                </>
              ) : (
                <p style={{ color: '#64748B', fontSize: '0.9rem' }}>Please log in to access student management</p>
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'directory', label: 'Student Directory', icon: <Users size={17} /> },
    { key: 'upload',    label: 'Bulk Upload',        icon: <Upload size={17} /> },
    { key: 'logs',      label: 'Upload Logs',         icon: <FileText size={17} /> },
  ];

  return (
    <div className="college-main-layout" style={{ background: 'var(--bg-main, #F8FAFC)' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>

            {/* -- Page Header -- */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '12px',
                background: 'rgba(79, 70, 229,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Users size={22} style={{ color: T.brand }} />
              </div>
              <div>
                <h1 style={{ fontSize: '1.625rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', margin: 0 }}>
                  Student Management
                </h1>
                <p style={{ color: '#64748B', fontSize: '0.875rem', margin: '2px 0 0' }}>
                  Manage your students with directory, bulk upload, and upload history
                </p>
              </div>
            </div>

            {/* -- Tab Bar -- */}
            <div style={{
              background: '#FFFFFF',
              border: T.border,
              borderRadius: T.radius.lg,
              marginBottom: 20,
              padding: '6px',
              display: 'flex',
              gap: 4,
            }}>
              {tabs.map(tab => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      borderRadius: T.radius.md,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 700 : 500,
                      transition: 'all 0.15s ease',
                      background: isActive ? '#EFF6FF' : 'transparent',
                      color: isActive ? 'var(--secondary-blue, #1E3A8A)' : '#64748B',
                      outline: 'none',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ color: isActive ? 'var(--secondary-blue, #1E3A8A)' : '#94A3B8' }}>{tab.icon}</span>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* -- Content Card -- */}
            <div style={{
              background: '#FFFFFF',
              borderRadius: T.radius.xl,
              border: T.border,
              padding: '24px',
              boxShadow: T.shadow.xs,
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* 4px top accent strip */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                background: activeTab === 'directory'
                  ? 'linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)'
                  : activeTab === 'upload'
                    ? 'linear-gradient(90deg, #059669, var(--primary-blue, #4F46E5))'
                    : 'linear-gradient(90deg, #D97706, #EA580C)',
                borderRadius: '18px 18px 0 0',
              }} />

              <div style={{ marginTop: 8 }}>
                {activeTab === 'directory' && (
                  <CollegeStudentDirectory
                    key={refreshKey}
                    collegeId={collegeId}
                    onEditStudent={handleEditStudent}
                    onDeleteStudent={handleDeleteStudent}
                    onRestoreStudent={handleRestoreStudent}
                    onResetPassword={handleResetPassword}
                    onBulkActionSuccess={handleModalSuccess}
                  />
                )}

                {activeTab === 'upload' && (
                  <CollegeStudentBulkUpload
                    collegeId={collegeId}
                    onUploadComplete={handleUploadComplete}
                    creditRefreshKey={refreshKey}
                  />
                )}

                {activeTab === 'logs' && (
                  <CollegeUploadLogs collegeId={collegeId} />
                )}
              </div>
            </div>

          </div>
        </main>
      </div>

      {/* -- Modals -- */}
      {activeModal === 'edit' && selectedStudent && (
        <EditStudentModal
          student={selectedStudent}
          onClose={closeModal}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'reset' && selectedStudent && (
        <ResetPasswordModal
          student={selectedStudent}
          onClose={closeModal}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'delete' && selectedStudent && (
        <DeleteStudentModal
          student={selectedStudent}
          onClose={closeModal}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'restore' && selectedStudent && (
        <RestoreStudentModal
          student={selectedStudent}
          onClose={closeModal}
          onSuccess={handleModalSuccess}
        />
      )}
      <CollegeFooter />
      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
      `}</style>
    </div>
  );
};

export default CollegeStudentManagement;


